import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { API, waNum, waLink, siteLogo, waList, igList, addrList, hoursList } from "@/lib/api";
import FloatingContact from "@/components/FloatingContact";
import BackgroundMusic from "@/components/BackgroundMusic";
import WaIcon from "@/components/WaIcon";
import { ShoppingCart, LayoutDashboard, LogOut, User, Menu, X, Instagram, Clock, MapPin } from "lucide-react";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/", label: "Beranda" },
  { to: "/tentang", label: "Tentang Kami" },
  { to: "/produk", label: "Produk" },
  { to: "/blog", label: "Blog" },
  { to: "/video", label: "Video" },
  { to: "/galeri", label: "Galeri" },
  { to: "/faq", label: "FAQ" },
  { to: "/lacak", label: "Lacak Pesanan" },
  { to: "/kontak", label: "Kontak" },
];

const FOOTER_NAV = [
  { to: "/produk", label: "Produk" },
  { to: "/tentang", label: "Tentang Kami" },
  { to: "/blog", label: "Blog" },
  { to: "/kontak", label: "Hubungi Kami" },
  { to: "/faq", label: "FAQ" },
];

export default function PublicLayout() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [site, setSite] = useState(null);

  useEffect(() => {
    API.get("/settings").then((r) => {
      setSite(r.data);
      document.documentElement.setAttribute("data-theme", r.data.theme || "navy-gold");
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-navy-dark">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
            <img src={siteLogo(site)} alt="NAWADAH by NDH Official" className="h-10 w-10 object-contain rounded-full bg-white" />
            <span className="leading-tight">
              <span className="block font-display text-xl tracking-wide text-white">NAWADAH</span>
              <span className="block text-[10px] uppercase tracking-[0.25em] text-gold">by NDH Official</span>
            </span>
          </Link>
          <nav className="hidden min-[1180px]:flex items-center gap-0" data-testid="main-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-2.5 py-2 text-xs xl:px-3 xl:text-sm font-medium rounded-md transition-colors ${
                    isActive ? "text-gold" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <NavLink
              to="/toko"
              data-testid="nav-toko"
              className={({ isActive }) =>
                `px-2.5 py-2 text-xs xl:px-3 xl:text-sm font-semibold rounded-md transition-colors ${isActive ? "text-gold" : "text-gold/80 hover:text-gold"}`
              }
            >
              Toko
            </NavLink>
          </nav>
          <div className="hidden min-[1180px]:flex items-center gap-1">
            <Link to="/keranjang" data-testid="nav-cart" className="relative p-2 text-slate-300 hover:text-white transition-colors">
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-gold text-[#0B1F3A] text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <>
                {user.role === "owner" && (
                  <Link to="/admin" data-testid="nav-dashboard" className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-200 hover:text-gold transition-colors">
                    <LayoutDashboard className="h-4 w-4" /> Dashboard
                  </Link>
                )}
                <Link to="/akun" data-testid="nav-account" className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-200 hover:text-white transition-colors">
                  <User className="h-4 w-4" /> {user.name?.split(" ")[0]}
                </Link>
                <button onClick={handleLogout} data-testid="nav-logout" className="p-2 text-slate-400 hover:text-red-400 transition-colors" title="Keluar">
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/masuk" data-testid="nav-login" className="px-4 py-2 text-sm font-medium text-slate-200 hover:text-white transition-colors">
                  Masuk
                </Link>
              </>
            )}
          </div>
          <button className="min-[1180px]:hidden p-2 text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <div className="min-[1180px]:hidden border-t border-white/10 px-4 py-3 space-y-1 bg-navy-dark" data-testid="mobile-menu">
            {[...NAV, { to: "/toko", label: "Toko" }, { to: "/keranjang", label: `Keranjang (${count})` }].map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-slate-200 hover:text-gold rounded-md">
                {n.label}
              </Link>
            ))}
            {user ? (
              <>
                {user.role === "owner" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-gold">
                    Dashboard Pemilik
                  </Link>
                )}
                <Link to="/akun" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-slate-200">
                  Akun Saya
                </Link>
                <button onClick={handleLogout} className="block w-full text-left px-3 py-2 text-sm text-red-400">
                  Keluar
                </button>
              </>
            ) : (
              <>
                <Link to="/masuk" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-slate-200">
                  Masuk
                </Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="relative bg-[#020811] border-t border-gold/20 mt-auto noise-overlay" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={siteLogo(site)} alt="NAWADAH by NDH Official" className="h-12 w-12 object-contain rounded-full bg-white" />
              <div>
                <p className="font-display text-lg text-white">NAWADAH</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-gold">by NDH Official</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-md">
              Supplier &amp; distributor tunggal Tali PP LUMBA LUMBA &amp; KUDA EMAS, Katrol LUMBA LUMBA, dan Timbangan (Manual &amp; Digital).
              Kualitas Sempurna dan Kepuasan Pelanggan adalah prioritas kami.
            </p>
          </div>
          <nav aria-label="Tautan situs">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Jelajahi</p>
            <ul className="space-y-3 text-sm text-slate-300">
              {FOOTER_NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="hover:text-gold transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Kontak</p>
            <ul className="space-y-3 text-sm text-slate-300">
              {waList(site).map((w, i) => (
                <li key={i}>
                  <a href={waLink("Halo Admin NAWADAH, saya ingin bertanya.", waNum(w.number))} target="_blank" rel="noreferrer" data-testid={i === 0 ? "footer-wa" : `footer-wa-${i}`} className="flex items-center gap-2 hover:text-gold transition-colors">
                    <WaIcon className="h-4 w-4 text-[#25D366]" /> {w.label || `Admin Nawadah ${i + 1}`}
                  </a>
                </li>
              ))}
              {igList(site).map((g, i) => (
                <li key={i}>
                  <a href={`https://instagram.com/${String(g.handle).replace(/^@/, "")}`} target="_blank" rel="noreferrer" data-testid={i === 0 ? "footer-ig" : `footer-ig-${i}`} className="flex items-center gap-2 hover:text-gold transition-colors">
                    <Instagram className="h-4 w-4 text-gold" /> {g.handle}
                  </a>
                </li>
              ))}
              <li className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-gold mt-0.5" />
                <span>
                  {hoursList(site).map((item, index) => <span key={index} className="block">{item.label ? `${item.label}: ` : ""}{item.hours}</span>)}
                </span>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Lokasi</p>
            <ul className="space-y-3 text-sm text-slate-300">
              {(addrList(site).length ? addrList(site) : [{ label: "", address: "Indonesia" }]).map((a, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`footer-addr-${i}`}>
                  <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                  <span>{a.label ? <strong className="text-slate-200">{a.label}: </strong> : null}{a.address}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} NAWADAH by NDH Official. Seluruh hak cipta dilindungi.
        </div>
      </footer>

      <FloatingContact site={site} />
      <BackgroundMusic settings={site} />
    </div>
  );
}
